import React from 'react';
import type { Status } from '~/components/atom/StatusIndicator';
import JustificationContainer from '~/components/atom/JustificationContainer';
import StatusIndicator from '~/components/atom/StatusIndicator';
import { Lfa_Account_Status } from '~/graphql/types';
import { reporter } from '~/hooks/useErrorReporter';
import { Body } from '~/components/atom/Typography';
import type { IconType } from '~/components/atom/Icon';
import Icon from '~/components/atom/Icon';

const text = {
  active: 'Actief',
  inactive: 'Inactief',
  unknownStatus: 'Onbekende status',
  activating: 'In activatie',
  deactivating: 'In deactivatie',
  activatingReady: 'In activatie',
  readyToActivate: 'Klaar om te activeren',
  manualActionNeeded: 'Handmatige acties nodig',
};

type Props = {
  status: Lfa_Account_Status;
  readyToActivate: boolean;
};

const StatusDisplay: React.FCC<Props> = ({ status, readyToActivate }) => (
  <JustificationContainer gap="xxs" align="center">
    <StatusIndicator status={getIndicatorStatus(status)} />
    {getStatusText(status, readyToActivate)}
  </JustificationContainer>
);

const getIndicatorStatus = (status: Lfa_Account_Status): Status => {
  switch (status) {
    case Lfa_Account_Status.Active:
      return 'active';
    case Lfa_Account_Status.Inactive:
      return 'inactive';
    case Lfa_Account_Status.Activating:
    case Lfa_Account_Status.Deactivating:
      return 'warning';
    default:
      reporter.captureException(
        new Error(`Unknown status for LFA settings: ${status}`),
      );
      return 'inactive';
  }
};

const getStatusText = (
  status: Lfa_Account_Status,
  readyToActivate: boolean,
): React.ReactNode => {
  switch (status) {
    case Lfa_Account_Status.Active:
      return text.active;
    case Lfa_Account_Status.Inactive:
      return text.inactive;
    case Lfa_Account_Status.Activating:
      return readyToActivate ? (
        <StatusLabel
          mainText={text.activatingReady}
          subText={text.readyToActivate}
          color="success"
          icon="check"
        />
      ) : (
        <StatusLabel
          mainText={text.activating}
          subText={text.manualActionNeeded}
          color="warning"
          icon="alert-circle"
        />
      );
    case Lfa_Account_Status.Deactivating:
      return (
        <StatusLabel
          mainText={text.deactivating}
          subText={text.manualActionNeeded}
          color="warning"
          icon="alert-circle"
        />
      );
    default:
      reporter.captureException(
        new Error(`Unknown status for LFA settings: ${status}`),
      );
      return text.unknownStatus;
  }
};

const StatusLabel: React.FC<{
  mainText: string;
  subText: string;
  color: 'danger' | 'success' | 'warning';
  icon: IconType;
}> = ({ mainText, subText, color, icon }) => (
  <JustificationContainer align="center" gap="xxs">
    {mainText}
    <JustificationContainer
      align="center"
      gap="xxs"
      backgroundColor={{ group: color, variant: 'translucent' }}
      padding={['xxs', 'xs']}
      border={{ radius: 's' }}
    >
      <Icon
        name={icon}
        size="s"
        strokeWidth={2.5}
        color={{ group: color, variant: 'dark' }}
      />
      <Body
        size="base"
        color={{ group: color, variant: 'dark' }}
        margin={[null]}
        fontWeight="medium"
      >
        {subText}
      </Body>
    </JustificationContainer>
  </JustificationContainer>
);

export default StatusDisplay;
